/**
 * Global top bar (48px). PRD §9.2:
 *   - Left: app logo + name
 *   - Center: workspace switcher (admin only)
 *   - Right: command-palette hint, theme toggle, user menu, in-call pulse
 *
 * The in-call pulse (013 Phase 6) is a compact pill that appears when any
 * voice call is in flight workspace-wide. Click navigates to the contact.
 */
import type { CrmLockState } from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { apiRequest } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { InFlightCallBanner } from '../admin/voice-agent/InFlightCallBanner';

import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';


interface Props {
  onOpenCommandPalette: () => void;
}

export function TopBar({ onOpenCommandPalette }: Props): JSX.Element {
  // 013 Phase 6: single workspace-wide lock query (3s poll) drives the
  // compact in-call indicator. Other surfaces (CrmPage, ContactDetailDrawer,
  // VoiceAgentSettingsCard) have their own pollers but share the cache via
  // the query key, so the polling is effectively de-duplicated.
  const lockQuery = useQuery({
    queryKey: queryKeys.crm.state(),
    queryFn: () => apiRequest<CrmLockState>('/crm/state'),
    refetchInterval: 3000,
  });

  return (
    <header className="relative flex h-topbar items-center justify-between border-b border-rule bg-paper px-3">
      <div className="flex min-w-0 items-center gap-2">
        {/* Logo image lives in /public so we reference it as an absolute path
            (no bundler import needed; the file is copied verbatim by Vite).
            The source PNG is white-on-transparent, so on the light theme we
            apply `invert` (filter: invert(100%)) to flip the white to dark
            and keep the mark visible against the light surface. */}
        <img
          src="/peerless-logo.png"
          alt="Peerless"
          className="h-6 w-auto shrink-0 select-none invert dark:invert-0"
          draggable={false}
        />
        <span className="text-sm font-medium tracking-tight text-ink">AI Portal</span>
      </div>

      {/* The switcher decides per-role whether to render and what tabs to show. */}
      <div className="absolute left-1/2 -translate-x-1/2">
        <WorkspaceSwitcher />
      </div>

      <div className="flex items-center gap-2">
        {lockQuery.data && lockQuery.data.inFlightCallId && (
          <InFlightCallBanner variant="compact" lockState={lockQuery.data} />
        )}
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className={
            'hidden items-center gap-1.5 rounded-tight border border-rule bg-paper-soft px-2.5 py-1 text-xs text-ink-muted ' +
            'transition-colors duration-instant ease-standard hover:text-ink sm:inline-flex'
          }
          aria-label="Open command palette"
        >
          <Search className="size-3.5" />
          <span>Quick nav</span>
          <kbd className="ml-1 rounded-tight border border-rule bg-paper px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
            {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
          </kbd>
        </button>
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
